import { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { DollarSign, TrendingUp, TrendingDown, Target, ShoppingBag } from "lucide-react";
import { getMyCommission, MyCommissionResponse } from "@/Api/sellerCommission";
import { formatCurrency } from "@/utils/format-currency";
import { cn } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { DashboardFilters } from "@/types/dashboard";

interface SellerCommissionCardSmallProps {
  period?: DashboardFilters["period"];
  startDate?: string;
  endDate?: string;
}

export function SellerCommissionCardSmall({ period, startDate, endDate }: SellerCommissionCardSmallProps) {
  const [data, setData] = useState<MyCommissionResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCommission();
  }, [period, startDate, endDate]);

  const loadCommission = async () => {
    setLoading(true);
    try {
      const response = await getMyCommission(period, startDate, endDate);
      if (response.data?.response) {
        setData(response.data.response);
      }
    } catch (error) {
      console.error("Erro ao carregar comissão:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="h-full border-0 shadow-lg bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
        <CardContent className="p-5">
          <div className="space-y-4">
            <Skeleton className="h-6 w-32 bg-white/20" />
            <Skeleton className="h-10 w-40 bg-white/20" />
            <Skeleton className="h-4 w-24 bg-white/20" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  const isPositiveChange = data.percent_change >= 0;
  const progress = data.goal_progress ?? 0;
  const progressClamped = Math.min(progress, 100);

  return (
    <Card className="h-full border-0 shadow-lg bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-white/20 backdrop-blur rounded-lg">
              <DollarSign className="h-4 w-4" />
            </div>
            <span className="font-medium">Comissão Atual</span>
          </div>
        </div>

        {/* Main Value */}
        <div className="mb-5">
          <p className="text-emerald-100 text-xs mb-1">Valor estimado</p>
          <p className="text-xl font-bold truncate">{formatCurrency(data.current_commission)}</p>
        </div>

        {/* Progress */}
        <div className="bg-white/10 backdrop-blur rounded-xl p-3 mb-4">
          <div className="flex items-center gap-1 mb-2">
            <Target className="h-3 w-3 text-emerald-200 flex-shrink-0" />
            <span className="text-xs text-emerald-100 truncate">Meta: {formatCurrency(data.goal_value)}</span>
            <span className="ml-auto text-xs font-bold flex-shrink-0">{progress.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2 mb-2">
            <div
              className="h-full rounded-full bg-white transition-all duration-500"
              style={{ width: `${progressClamped}%` }}
            />
          </div>
          <p className="text-xs text-emerald-200 truncate flex items-center gap-1">
            {isPositiveChange ? (
              <TrendingUp className="w-3 h-3" />
            ) : (
              <TrendingDown className="w-3 h-3" />
            )}
            <span className={cn(isPositiveChange ? "text-white" : "text-red-200")}>
              {isPositiveChange ? "+" : ""}{data.percent_change}%
            </span>
            <span>vs mês anterior</span>
          </p>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center gap-2 pt-3 border-t border-white/20">
          <ShoppingBag className="h-4 w-4 text-emerald-200" />
          <span className="text-sm text-emerald-100">
            Taxa: <span className="font-semibold text-white">{data.rate_applied}%</span>
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
